/**
 * Typed fetch wrapper for the KFMS API (see kfms/docs/API-CONVENTIONS.md).
 *
 *  - Bearer access token from memory, refreshed once on 401 (single-flight) via the `kfms_rt` cookie
 *  - `Accept-Language` from the current UI language
 *  - problem+json → {@link ApiError} (code, status, detail, params, errors[], details.blockers)
 *  - `{ data }` / `{ data, meta }` envelopes unwrapped by the helpers
 *  - `idempotent: true` → `Idempotency-Key: <uuid>`; `ifMatch: version` → `If-Match`
 *
 * Paths may be written as `/api/v1/core/items`, `/core/items` or `core/items` — all resolve to the same URL.
 *
 * @example
 *   const item = await api.get<Item>('/core/items/123');
 *   const { data, meta } = await api.list<Item>('/core/items', { page: 1, q: 'cola' });
 *   await api.patch(`/core/items/${id}`, values, { ifMatch: item.version });
 *   await api.post(`/fin/journal-entries/${id}:post`, undefined, { idempotent: true });
 */
import { tokenStore } from './tokenStore';
import { uuid } from './uuid';

// ---------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------

export interface ListMeta {
  page: number;
  pageSize: number;
  total: number;
  [key: string]: unknown;
}

export interface ListResponse<T> {
  data: T[];
  meta: ListMeta;
}

export type QueryPrimitive = string | number | boolean | Date;
export type QueryValue = QueryPrimitive | null | undefined | ReadonlyArray<QueryPrimitive>;
export type QueryParams = Record<string, QueryValue>;

export interface FieldError {
  path: string;
  message: string;
}

/** Gate-style failure reason (`details.blockers[]`, e.g. production start). */
export interface Blocker {
  code: string;
  messageAr: string;
  messageEn: string;
  [key: string]: unknown;
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status: number;
  code: string;
  detail?: string;
  params?: Record<string, unknown>;
  errors?: FieldError[];
  details?: Record<string, unknown> & { blockers?: Blocker[] };
  requestId?: string;
}

/** Error thrown for every non-2xx response and for network failures (`status 0`, code `NETWORK_ERROR`). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title?: string;
  readonly detail?: string;
  readonly type?: string;
  readonly params: Record<string, unknown>;
  readonly errors: FieldError[];
  readonly details: Record<string, unknown> & { blockers?: Blocker[] };
  readonly requestId?: string;

  constructor(problem: ProblemDetails, options?: { cause?: unknown }) {
    super(problem.detail || problem.title || problem.code, options);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.title = problem.title;
    this.detail = problem.detail;
    this.type = problem.type;
    this.params = problem.params ?? {};
    this.errors = Array.isArray(problem.errors) ? problem.errors : [];
    this.details = problem.details && typeof problem.details === 'object' ? problem.details : {};
    this.requestId = problem.requestId;
  }

  /** `details.blockers` (empty array when absent). */
  get blockers(): Blocker[] {
    const b = this.details.blockers;
    return Array.isArray(b) ? b : [];
  }
  get isNetworkError(): boolean {
    return this.status === 0;
  }
  get isValidation(): boolean {
    return this.status === 422;
  }
  get isVersionConflict(): boolean {
    return this.code === 'CONFLICT_VERSION';
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  static network(cause: unknown): ApiError {
    return new ApiError({ status: 0, code: 'NETWORK_ERROR', title: 'Network error' }, { cause });
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export interface RequestOptions {
  query?: QueryParams;
  /** JSON-serialisable value, `FormData`, `Blob` or string. */
  body?: unknown;
  headers?: Record<string, string>;
  /** Adds `Idempotency-Key: <uuid>` (or the explicit `idempotencyKey`). */
  idempotent?: boolean;
  idempotencyKey?: string;
  /** Adds `If-Match: <version>` for optimistic concurrency. */
  ifMatch?: string | number | null;
  signal?: AbortSignal;
  /** Send the Bearer token (default true). */
  auth?: boolean;
  /** Try `POST /auth/refresh` once on 401 and retry (default true). */
  retryOn401?: boolean;
}

// ---------------------------------------------------------------------------------------------
// Configuration hooks (set by AuthProvider / i18n)
// ---------------------------------------------------------------------------------------------

const API_PREFIX = '/api/v1';
const API_ORIGIN: string = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';

type HeaderProvider = () => Record<string, string>;
const headerProviders = new Set<HeaderProvider>();
let onAuthFailure: (() => void) | null = null;
let getLanguage: () => string = () => (typeof document !== 'undefined' && document.documentElement.lang) || 'ar';

export const apiConfig = {
  /** Extra headers on every request. Returns an unsubscribe fn. */
  addHeaderProvider(p: HeaderProvider): () => void {
    headerProviders.add(p);
    return () => headerProviders.delete(p);
  },
  /** Called when a refresh fails → the app returns to the login screen. */
  setAuthFailureHandler(fn: (() => void) | null): void {
    onAuthFailure = fn;
  },
  setLanguageGetter(fn: () => string): void {
    getLanguage = fn;
  },
};

// ---------------------------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------------------------

/** Normalises `core/items`, `/core/items`, `/api/v1/core/items` → `/api/v1/core/items`. */
export function apiPath(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/api/')) return path;
  return `${API_PREFIX}${path.startsWith('/') ? '' : '/'}${path}`;
}

function serialiseValue(v: QueryPrimitive): string {
  return v instanceof Date ? v.toISOString() : String(v);
}

/** Builds a query string; arrays are comma-joined (`status=DRAFT,SUBMITTED`), empty values skipped. */
export function buildQuery(query?: QueryParams): string {
  if (!query) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      sp.set(k, v.map((x) => serialiseValue(x as QueryPrimitive)).join(','));
    } else {
      sp.set(k, serialiseValue(v as QueryPrimitive));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function apiUrl(path: string, query?: QueryParams): string {
  const p = apiPath(path);
  const base = /^https?:\/\//i.test(p) ? p : `${API_ORIGIN}${p}`;
  const qs = buildQuery(query);
  if (!qs) return base;
  return base.includes('?') ? `${base}&${qs.slice(1)}` : `${base}${qs}`;
}

// ---------------------------------------------------------------------------------------------
// Refresh (single-flight)
// ---------------------------------------------------------------------------------------------

let refreshInFlight: Promise<boolean> | null = null;

function commonHeaders(): Record<string, string> {
  const h: Record<string, string> = { Accept: 'application/json', 'Accept-Language': getLanguage() };
  headerProviders.forEach((p) => Object.assign(h, p()));
  return h;
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(apiUrl('/auth/refresh'), { method: 'POST', credentials: 'include', headers: commonHeaders() });
    if (!res.ok) {
      tokenStore.clear();
      return false;
    }
    const json = (await res.json()) as { data?: { accessToken?: string; expiresIn?: number } };
    if (!json.data?.accessToken) {
      tokenStore.clear();
      return false;
    }
    tokenStore.set(json.data.accessToken, json.data.expiresIn);
    return true;
  } catch {
    // network failure: keep the current token (temporary outage) and report failure
    return false;
  }
}

/** Refreshes the access token using the HttpOnly cookie. Concurrent callers share one request. */
export function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) refreshInFlight = doRefresh().finally(() => (refreshInFlight = null));
  return refreshInFlight;
}

// ---------------------------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------------------------

function isAuthEndpoint(path: string): boolean {
  const p = apiPath(path);
  return /\/api\/v1\/auth\/(login|refresh|mfa|activate|password\/(forgot|reset|change-required)|device-login)/.test(p);
}

async function parseProblem(res: Response): Promise<ApiError> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('json')) {
    try {
      const body = (await res.json()) as Partial<ProblemDetails>;
      return new ApiError({
        ...body,
        status: body.status ?? res.status,
        code: body.code ?? `HTTP_${res.status}`,
      });
    } catch {
      /* fall through */
    }
  }
  return new ApiError({ status: res.status, code: `HTTP_${res.status}`, title: res.statusText });
}

/**
 * Low-level request returning the raw `Response` (after auth refresh/retry and error mapping).
 * Throws {@link ApiError} for non-2xx responses and network errors.
 */
export async function rawRequest(method: string, path: string, opts: RequestOptions = {}): Promise<Response> {
  const { query, body, headers, idempotent, idempotencyKey, ifMatch, signal, auth = true } = opts;
  const retryOn401 = opts.retryOn401 ?? !isAuthEndpoint(path);
  const url = apiUrl(path, query);
  const idemKey = idempotencyKey ?? (idempotent ? uuid() : undefined); // stable across the 401 retry

  const send = async (): Promise<Response> => {
    const h: Record<string, string> = { ...commonHeaders(), ...headers };
    let payload: BodyInit | undefined;
    if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
      payload = body as BodyInit;
    } else if (body !== undefined) {
      payload = JSON.stringify(body);
      h['Content-Type'] = 'application/json';
    }
    if (auth) {
      const token = tokenStore.get();
      if (token) h.Authorization = `Bearer ${token}`;
    }
    if (idemKey) h['Idempotency-Key'] = idemKey;
    if (ifMatch !== undefined && ifMatch !== null) h['If-Match'] = String(ifMatch);
    try {
      return await fetch(url, { method, headers: h, body: payload, credentials: 'include', signal });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw ApiError.network(e);
    }
  };

  // Pro-active refresh shortly before expiry avoids a guaranteed 401 round-trip.
  if (auth && retryOn401 && tokenStore.isExpiring()) await refreshAccessToken();

  let res = await send();
  if (res.status === 401 && auth && retryOn401) {
    const ok = await refreshAccessToken();
    if (ok) res = await send();
    else if (res.status === 401) {
      const err = await parseProblem(res);
      onAuthFailure?.();
      throw err;
    }
  }
  if (!res.ok) throw await parseProblem(res);
  return res;
}

/** Performs a request and returns the parsed JSON body (or `undefined` for 204 / empty bodies). */
export async function request<T = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await rawRequest(method, path, opts);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

// ---------------------------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------------------------

/** Extracts the file name from `Content-Disposition` (supports RFC 5987 `filename*`). */
export function filenameFromDisposition(header: string | null, fallback = 'download'): string {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(header);
  if (star?.[2]) {
    try {
      return decodeURIComponent(star[2].trim().replace(/^"|"$/g, ''));
    } catch {
      /* ignore */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

export interface FileResult {
  blob: Blob;
  filename: string;
  contentType: string;
}

export async function fetchFile(
  path: string,
  opts: RequestOptions & { method?: string; filename?: string } = {},
): Promise<FileResult> {
  const res = await rawRequest(opts.method ?? 'GET', path, {
    ...opts,
    headers: { Accept: '*/*', ...opts.headers },
  });
  const blob = await res.blob();
  return {
    blob,
    filename: opts.filename ?? filenameFromDisposition(res.headers.get('content-disposition')),
    contentType: res.headers.get('content-type') ?? blob.type,
  };
}

/** Saves a blob via a temporary `<a download>`. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Downloads a file endpoint (list export, template, attachment…) with auth headers. */
export async function download(
  path: string,
  opts: RequestOptions & { method?: string; filename?: string } = {},
): Promise<void> {
  const f = await fetchFile(path, opts);
  saveBlob(f.blob, f.filename);
}

/**
 * Opens an authenticated file (typically a PDF) in a new tab. The tab is opened synchronously
 * (popup-blocker safe) and pointed at a blob URL once loaded.
 */
export async function openFile(path: string, opts: RequestOptions = {}): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const f = await fetchFile(path, opts);
    const url = URL.createObjectURL(f.blob);
    if (win) win.location.href = url;
    else saveBlob(f.blob, f.filename);
    setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
  } catch (e) {
    win?.close();
    throw e;
  }
}

/** Multipart upload. `fields` are appended as text parts next to `file`. */
export async function upload<T = unknown>(
  path: string,
  file: File | Blob | FormData,
  fields: Record<string, string | number | boolean | null | undefined> = {},
  opts: RequestOptions = {},
): Promise<T> {
  let form: FormData;
  if (file instanceof FormData) form = file;
  else {
    form = new FormData();
    form.append('file', file);
  }
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== null) form.append(k, String(v));
  return unwrap<T>(await request('POST', path, { ...opts, body: form }));
}

// ---------------------------------------------------------------------------------------------
// Convenience helpers (envelope unwrapped)
// ---------------------------------------------------------------------------------------------

export function get<T = unknown>(path: string, query?: QueryParams, opts: RequestOptions = {}): Promise<T> {
  return request('GET', path, { ...opts, query: { ...opts.query, ...query } }).then((b) => unwrap<T>(b));
}

export async function list<T = unknown>(path: string, query?: QueryParams, opts: RequestOptions = {}): Promise<ListResponse<T>> {
  const body = await request<{ data?: T[]; meta?: ListMeta } | T[]>('GET', path, { ...opts, query: { ...opts.query, ...query } });
  if (Array.isArray(body)) return { data: body, meta: { page: 1, pageSize: body.length, total: body.length } };
  const data = Array.isArray(body?.data) ? body.data : [];
  return { data, meta: body?.meta ?? { page: 1, pageSize: data.length, total: data.length } };
}

export function post<T = unknown>(path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  return request('POST', path, { ...opts, body }).then((b) => unwrap<T>(b));
}

export function put<T = unknown>(path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  return request('PUT', path, { ...opts, body }).then((b) => unwrap<T>(b));
}

export function patch<T = unknown>(path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  return request('PATCH', path, { ...opts, body }).then((b) => unwrap<T>(b));
}

export function del<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  return request('DELETE', path, opts).then((b) => unwrap<T>(b));
}

export const api = { request, rawRequest, get, list, post, put, patch, del, download, upload, fetchFile, openFile, url: apiUrl };
